import { Injectable, NgZone, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Subject } from 'rxjs';

/**
 * Emits whenever the app comes back to the foreground - tab made visible on the web, app resumed
 * on mobile.
 *
 * Screens used to refresh only when a push notification arrived, so a request whose status had
 * changed while the app sat in the background kept showing its old status until the next push.
 * Anything that can go stale should listen here rather than trusting notifications to be
 * delivered: a push can be dropped, delayed, or refused by the user.
 */
@Injectable({ providedIn: 'root' })
export class AppActivityService {
    private readonly _zone = inject(NgZone);

    /** Fires when the app becomes visible again after being hidden or backgrounded. */
    readonly resumed$ = new Subject<void>();

    constructor() {
        if (Capacitor.isNativePlatform()) {
            App.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    // Capacitor listeners fire outside Angular's zone; without this, subscribers
                    // update state that never triggers change detection.
                    this._zone.run(() => this.resumed$.next());
                }
            });
            return;
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.resumed$.next();
            }
        });
    }
}
